'use client';

import {
  useCallback,
  useEffect,
  useState,
  useRef,
  useLayoutEffect
} from 'react';
import type { Dispatch, SetStateAction } from 'react';

declare global {
  interface WindowEventMap {
    'local-storage': CustomEvent;
  }
}

export type UseLocalStorageOptions<T> = {
  serializer?: (value: T) => string;
  deserializer?: (value: string) => T;
  initializeWithValue?: boolean;
};

const IS_SERVER = typeof window === 'undefined';

const useIsomorphicLayoutEffect = IS_SERVER ? useEffect : useLayoutEffect;

export function useLocalStorage<T>(
  key: string,
  initialValue: T | (() => T),
  options: UseLocalStorageOptions<T> = {}
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const { initializeWithValue = true } = options;

  const serializer = useCallback(
    (value: T): string => {
      if (options.serializer) {
        return options.serializer(value);
      }
      return JSON.stringify(value);
    },
    [options]
  );

  const deserializer = useCallback(
    (value: string): T => {
      if (options.deserializer) {
        return options.deserializer(value);
      }

      if (value === 'undefined') {
        return undefined as unknown as T;
      }

      const defaultValue =
        initialValue instanceof Function ? initialValue() : initialValue;

      try {
        return JSON.parse(value) as T;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error parsing JSON:', error);
        return defaultValue;
      }
    },
    [options, initialValue]
  );

  const readValue = useCallback((): T => {
    const initialValueToUse =
      initialValue instanceof Function ? initialValue() : initialValue;

    if (IS_SERVER) {
      return initialValueToUse;
    }

    try {
      const raw = window.localStorage.getItem(key);
      return raw !== null ? deserializer(raw) : initialValueToUse;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValueToUse;
    }
  }, [initialValue, key, deserializer]);

  const [storedValue, setStoredValue] = useState(() => {
    if (initializeWithValue) {
      return readValue();
    }
    return initialValue instanceof Function ? initialValue() : initialValue;
  });

  const setValueRef = useRef<Dispatch<SetStateAction<T>> | undefined>(
    undefined
  );

  setValueRef.current = (value: SetStateAction<T>) => {
    if (IS_SERVER) {
      return;
    }

    try {
      const newValue = value instanceof Function ? value(readValue()) : value;

      window.localStorage.setItem(key, serializer(newValue));
      setStoredValue(newValue);

      window.dispatchEvent(new StorageEvent('local-storage', { key }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    (value: SetStateAction<T>) => setValueRef.current?.(value),
    []
  );

  const removeValue = useCallback(() => {
    if (IS_SERVER) {
      return;
    }

    const defaultValue =
      initialValue instanceof Function ? initialValue() : initialValue;

    window.localStorage.removeItem(key);
    setStoredValue(defaultValue);

    window.dispatchEvent(new StorageEvent('local-storage', { key }));
  }, [key, initialValue]);

  useEffect(() => {
    setStoredValue(readValue());
  }, [key, readValue]);

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      const storageEvent = event as StorageEvent;
      if (storageEvent.key && storageEvent.key !== key) {
        return;
      }
      setStoredValue(readValue());
    },
    [key, readValue]
  );

  useIsomorphicLayoutEffect(() => {
    if (IS_SERVER) return;

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('local-storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('local-storage', handleStorageChange);
    };
  }, [handleStorageChange]);

  return [storedValue, setValue, removeValue];
}

export function dispatchStorageEvent(key: string): void {
  if (!IS_SERVER) {
    window.dispatchEvent(new StorageEvent('local-storage', { key }));
  }
}
